import { Suspense } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { LoadingBlock } from '@/components/ui/loading-block';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { useChatRealtimeInvalidator } from '@/features/chat/hooks';
import { useLeadsRealtimeInvalidator } from '@/features/leads/hooks';
import { WelcomeDialog } from '@/features/onboarding/WelcomeDialog';
import { NewProspectChoiceDialog } from '@/features/prospects/components/NewProspectChoiceDialog';
import { BackgroundJobsTray } from '@/features/shared/components/BackgroundJobsTray';
import { GlobalRagChat } from '@/features/shared/components/GlobalRagChat';
import { useTasksRealtimeInvalidator } from '@/features/tasks/hooks';
import { useNewProspectModal } from '@/stores/useNewProspectModal';

import { useScreenLayout } from '@/app/routeLayout';
import { AppSidebar } from './AppSidebar';
import { Atmosphere } from './Atmosphere';
import { Topbar } from './Topbar';
import { ConfirmProvider } from '@/components/ui/confirm-dialog';
import { PromptProvider } from '@/components/ui/prompt-dialog';

/**
 * Layout autenticado usando el patrón oficial de shadcn/ui Sidebar.
 *
 * - `SidebarProvider` maneja state (cookie persistente + responsive).
 * - `AppSidebar` default `variant="sidebar"` (plano, sin recuadro/margin).
 * - `SidebarInset` redistribuye automáticamente el contenido cuando
 *   el sidebar se colapsa.
 * - En mobile el sidebar se convierte en off-canvas drawer.
 *
 * El prototipo Claude Design NO usa el efecto "card flotante" (variant=inset)
 * — sidebar pegado al borde izquierdo, contenido full width, transparent
 * background para que se vea la capa Atmosphere.
 */
export function AppLayout() {
  // Mount global · suscribe a las tablas core.task_* y invalida los
  // queries de TanStack Query cuando llega un cambio. La campanita y
  // las páginas de tareas se refrescan solas sin tener que pollear.
  useTasksRealtimeInvalidator();
  useChatRealtimeInvalidator();
  useLeadsRealtimeInvalidator();

  // Modal global "Crear nuevo prospecto" (IA o manual). Controlado por el store
  // así lo abren tanto el botón de la lista como el ⌘K y el dashboard.
  const newProspect = useNewProspectModal();

  const location = useLocation();
  // El tipo de pantalla lo declara la RUTA (`handle: { layout }`), no una lista
  // de prefijos: el `startsWith` viejo hacía que marcar `/settings` arrastrara
  // sus 8 subpantallas de formulario. Ver `src/app/routeLayout.ts`.
  const layout = useScreenLayout();
  // /chat pelea con el composer y /sol ya ES la experiencia de Sol — en ambos
  // ocultamos el FAB flotante para no duplicar la affordance de IA. El resto
  // de full-bleed (ej. /tareas/:id) sigue mostrando el chat IA flotante.
  const hideRagChat =
    location.pathname.startsWith('/chat') || location.pathname.startsWith('/sol');

  return (
    <ConfirmProvider>
      <PromptProvider>
    <SidebarProvider defaultOpen>
      <Atmosphere />
      <AppSidebar />
      <SidebarInset className="bg-transparent min-w-0">
        <Topbar />
        {/* IMPORTANTE: NO usar `overflow-x-hidden` acá. Crea un containing block
            que rompe `position: sticky` de los hijos (tabs sticky de prospect).
            Usamos `overflow-x-clip` que oculta el overflow horizontal sin
            crear containing block.

            min-h-0 es necesario para que `flex-1` colapse en pantallas con
            children que usen `h-[100dvh]` y `overflow-y-auto` (chat). Sin
            esto, el flex parent no comprime y el scroll se va al body. */}
        {/* `div` y no `main`: `SidebarInset` ya renderiza un `<main>`, así que
            había dos landmarks anidados y un lector de pantalla anunciaba dos
            contenidos principales. */}
        <div className="flex-1 min-h-0 min-w-0 overflow-x-clip">
          {/* Un solo límite para las pantallas diferidas, por FUERA del cambio
              de tipo de pantalla: queda montado entre rutas, así la
              transición de la navegación sigue mostrando la pantalla anterior
              mientras llega el chunk. Adentro de cada rama, pasar de una
              pantalla de trabajo a un documento montaría un límite nuevo y
              mostraría el indicador en cada salto. */}
          <Suspense fallback={<LoadingBlock className="m-4">Cargando la pantalla…</LoadingBlock>}>
            {layout === 'work' ? (
              /*
               * Pantalla de trabajo: LA CAJA LA DA EL SHELL. Ocupa el alto de la
               * ventana menos la topbar y no desborda; la pantalla adentro solo
               * aporta `flex-1 min-h-0` y el `min-h-0` de su cadena interna.
               * Antes cada pantalla declaraba su propia altura con
               * `h-[calc(100dvh-3.5rem)]`, o sea que el número vivía repetido en
               * cada archivo y nadie podía cambiarlo de un lugar.
               */
              <div className="flex h-[calc(100dvh-var(--topbar-h))] min-h-[500px] flex-col overflow-hidden">
                <Outlet />
              </div>
            ) : layout === 'gesture' ? (
              /* Una sola acción o un solo mensaje: tarjeta angosta centrada
                 dentro del área útil, con el sidebar en su lugar. */
              <div className="animate-fade-in mx-auto flex w-full max-w-[520px] flex-col justify-center px-4 py-10">
                <Outlet />
              </div>
            ) : (
              /* Documento: byte a byte como estaba. El navegador desplaza la
                 página y el ancho queda acotado a medida de lectura. */
              <div className="animate-fade-in mx-auto w-full max-w-[1320px] px-4 pt-4 pb-[var(--sol-safe)] lg:px-6">
                <Outlet />
              </div>
            )}
          </Suspense>
        </div>
      </SidebarInset>
      {/* Onboarding · modal de bienvenida (Anexo E · E6). Se muestra una
          sola vez al empleado en su primer ingreso, en cualquier pantalla.
          Degrada graceful a localStorage si core.user_onboarding no existe. */}
      <WelcomeDialog />
      {/* Modal global · "Crear nuevo prospecto" (IA o manual). Único en toda la
          app; lo disparan el botón de la lista, ⌘K y las acciones rápidas. */}
      <NewProspectChoiceDialog
        open={newProspect.open}
        onOpenChange={newProspect.setOpen}
      />
      {/* Tray flotante bottom-right · jobs async (deep search, generación campaña, etc) */}
      <BackgroundJobsTray />
      {/* Chat IA flotante · presente en toda la app EXCEPTO en /chat (chocaría
          con el composer y duplicaría la affordance de IA). Scope automático:
          prospect-aware cuando estás en /prospects/:id, modo libre en cualquier
          otra ruta. */}
      {!hideRagChat && <GlobalRagChat />}
    </SidebarProvider>
      </PromptProvider>
    </ConfirmProvider>
  );
}
